(() => {
  'use strict';

  document.documentElement.classList.add('sr-js');

  // Locale/market aware. `window.Shopify.routes.root` is "/" on a plain store and
  // "/en-gb/" or similar under markets — hardcoding "/cart/add.js" 404s there.
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const url = (path) => `${ROOT.replace(/\/$/, '')}/${path}`;
  const CART_SECTION = 'sr-cart-drawer';

  const surfaceStack = [];
  const cartMutationQueue = [];
  const restoreFocusTargets = new WeakMap();
  let cartRequestInFlight = false;
  let pendingFocusRestore = null;
  let toastTimer = 0;

  const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const one = (selector, root = document) => root.querySelector(selector);

  function showToast(message) {
    const toast = one('[data-toast-region]');
    if (!toast || !message) return;
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    toastTimer = window.setTimeout(() => {
      toast.hidden = true;
      toast.textContent = '';
    }, 3200);
  }

  /* ------------------------------------------------------------------ surfaces */

  /* `!node.contains(activeDialog)` is what makes a nested modal possible, and without it
     the variant picker is born dead.

     The picker <dialog> lives INSIDE the cart drawer <dialog>, because it is rendered by
     the cart section and has to be replaced along with it on every mutation. So when the
     picker opens, this loop walks `body > *`, finds the cart drawer — a body child that
     is not the picker — and sets `inert` on it. `inert` inherits to descendants, so the
     picker itself would go inert: visible, focus-trapped, and completely unclickable.

     Skipping ancestors is safe because `showModal()` already makes everything outside the
     top layer non-interactive natively. This loop is belt-and-braces for assistive tech,
     not the only barrier. */
  function setBackgroundInert(activeDialog, inert) {
    all('body > *').forEach((node) => {
      if (node !== activeDialog
          && !node.contains(activeDialog)
          && !node.matches('[data-toast-region], script')) {
        node.inert = inert;
      }
    });
  }

  function openSurface(dialog, trigger) {
    if (!dialog || dialog.open) return;
    if (trigger) restoreFocusTargets.set(dialog, trigger);
    if (trigger && trigger.matches('[aria-expanded]')) trigger.setAttribute('aria-expanded', 'true');
    setBackgroundInert(dialog, true);
    document.body.classList.add('sr-scroll-lock');
    surfaceStack.push(dialog);
    dialog.showModal();
    window.requestAnimationFrame(() => {
      const target = one('[data-autofocus]', dialog) || one('button, a, input, select', dialog);
      if (target) target.focus();
    });
  }

  function closeSurface(dialog) {
    if (!dialog || !dialog.open) return;
    const index = surfaceStack.lastIndexOf(dialog);
    if (index !== -1) surfaceStack.splice(index, 1);
    dialog.close();
    const trigger = restoreFocusTargets.get(dialog) || null;
    if (trigger && trigger.matches('[aria-expanded]')) trigger.setAttribute('aria-expanded', 'false');
    const nextSurface = surfaceStack[surfaceStack.length - 1];
    if (nextSurface) {
      setBackgroundInert(nextSurface, true);
    } else {
      all('body > *').forEach((node) => { node.inert = false; });
      document.body.classList.remove('sr-scroll-lock');
    }
    // The bag trigger is never replaced by a section swap, so this reference stays live.
    if (trigger && trigger.disabled) {
      pendingFocusRestore = trigger;
    } else if (trigger) {
      trigger.focus();
    }
  }

  /* DELEGATED, not bound per element. The variant-picker sheets are rendered by the cart
     section and therefore destroyed and recreated on every cart mutation — anything bound
     to them at load would be discarded by the first add to cart, leaving sheets that
     could not be dismissed by Esc or by clicking the backdrop.

     `cancel` and `close` do not bubble, so they are caught in the CAPTURE phase, which
     still reaches document on the way down to the target. */
  const isSurface = (node) => node instanceof HTMLDialogElement && node.matches('[data-surface]');

  document.addEventListener('cancel', (event) => {
    if (!isSurface(event.target)) return;
    event.preventDefault();
    if (surfaceStack[surfaceStack.length - 1] === event.target) closeSurface(event.target);
  }, true);

  // Belt and braces: a <dialog> can be closed by things that never reach closeSurface
  // — a programmatic .close(), a form with method="dialog", a future third-party. If
  // that happens the scroll lock and the `inert` flags would stay on and the page
  // would silently stop accepting clicks. Reconcile on the native close event.
  document.addEventListener('close', (event) => {
    if (!isSurface(event.target)) return;
    const index = surfaceStack.lastIndexOf(event.target);
    if (index === -1) return;                // closeSurface already handled it
    surfaceStack.splice(index, 1);
    const nextSurface = surfaceStack[surfaceStack.length - 1];
    if (nextSurface) {
      setBackgroundInert(nextSurface, true);
    } else {
      all('body > *').forEach((node) => { node.inert = false; });
      document.body.classList.remove('sr-scroll-lock');
    }
  }, true);

  document.addEventListener('click', (event) => {
    if (!isSurface(event.target)) return;
    if (surfaceStack[surfaceStack.length - 1] === event.target) closeSurface(event.target);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const topSurface = surfaceStack[surfaceStack.length - 1];
    if (topSurface) {
      event.preventDefault();
      closeSurface(topSurface);
    }
  });

  const navDialog = one('#sr-nav-drawer');
  const cartDialog = one('#sr-cart-drawer');

  all('[data-nav-open]').forEach((trigger) => {
    trigger.addEventListener('click', () => openSurface(navDialog, trigger));
  });
  all('[data-cart-open]').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      if (!cartDialog) return;
      event.preventDefault();
      openSurface(cartDialog, trigger);
    });
  });
  // Delegated: the close button inside the drawer survives, but keeping this delegated
  // means a future swap of the header row cannot silently break it.
  /* ---------------------------------------------------- deferred variant picker

     Each cross-sell card in the bag carries exactly ONE add control, and for a product
     with more than one size that control is an <a href> to the product page — which is
     the correct destination with the script removed. Here we upgrade it in place: open
     the pre-rendered sheet instead of navigating.

     Delegated because the sheets and their triggers are replaced on every mutation. If
     the sheet is missing for any reason we do NOT preventDefault, so the anchor falls
     back to loading the PDP rather than becoming a dead control. */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-variant-pick]');
    if (!trigger) return;
    const sheet = document.getElementById(trigger.dataset.variantPick);
    if (!sheet) return;
    event.preventDefault();
    openSurface(sheet, trigger);
  });

  document.addEventListener('click', (event) => {
    const closer = event.target.closest('[data-surface-close]');
    if (closer) closeSurface(closer.closest('dialog'));
  });

  /* ------------------------------------------------------------------ cart state */

  function setCartBusy(busy) {
    all('[data-cart-mutation], [data-product-form] button[type="submit"], [data-checkout]')
      .forEach((control) => {
        // data-permanently-disabled is what keeps sold-out sizes disabled. Without it this
        // line re-enables every sold-out button after the first successful add.
        control.disabled = busy || control.dataset.permanentlyDisabled === 'true';
        control.setAttribute('aria-busy', String(busy));
      });
    // Checkout has to go inert too, or a shopper can check out against pre-mutation state
    // while the quantity change they just made is still in flight.
    //
    // The VIEW BAG link — and the tab-order guard it needed, because a link cannot be
    // `disabled` and `pointer-events: none` leaves it keyboard-activatable — went with it
    // on 2026-08-18. Nothing in the drawer navigates away mid-mutation now, so there is no
    // link left to pull out of the tab order. The capture-phase aria-disabled guard below
    // still covers anything that grows one later.
    if (!busy && pendingFocusRestore) {
      const target = pendingFocusRestore;
      pendingFocusRestore = null;
      target.focus();
    }
  }

  // aria-disabled is advisory: it tells assistive tech and stops nothing. Enforce it for
  // mouse AND keyboard, in the capture phase so it wins before any other handler.
  document.addEventListener('click', (event) => {
    if (event.target.closest('[aria-disabled="true"]')) event.preventDefault();
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (event.target.closest('[aria-disabled="true"]')) event.preventDefault();
  }, true);

  /* --------------------------------------------------------------- focus after a swap

     Every mutation replaces [data-cart-render] wholesale, which destroys the button the
     shopper was using and drops focus to <body> INSIDE an open modal.

     The token is captured SYNCHRONOUSLY in the click/submit handler. Capturing it later
     is too late: setCartBusy(true) disables the clicked control, and disabling a focused
     element has already moved focus to <body> by then.

     Restoration is decided on RESPONSE-time state, never request-time. A request can
     outlive the user's attention — they may close the drawer or tab somewhere else while
     it is in flight, and restoring blindly would yank them back into replaced markup.
  */
  function captureFocusToken(control) {
    if (!control || !cartDialog || !cartDialog.contains(control)) return null;
    const line = control.closest('[data-cart-line]');
    return {
      key: line ? line.dataset.itemKey : null,
      action: control.dataset ? control.dataset.cartMutation : null
    };
  }

  function restoreCartFocus(token) {
    if (!token || !cartDialog || !cartDialog.open) return;
    const active = document.activeElement;
    const lost = !active || active === document.body || !document.contains(active)
      || active.closest('[data-cart-render]') === null;
    if (!lost) return;
    let target = null;
    if (token.key && token.action) {
      target = one(`[data-item-key="${CSS.escape(token.key)}"] `
        + `[data-cart-mutation="${token.action}"]`, cartDialog);
    }
    target = target
      || one('[data-cart-line] [data-cart-mutation="increase"]', cartDialog)
      || one('[data-checkout]', cartDialog)
      || one('[data-surface-close]', cartDialog);
    if (!target) return;
    if (!target.hasAttribute('tabindex') && !target.matches('a, button, input, select')) {
      target.setAttribute('tabindex', '-1');
    }
    target.focus();
  }

  function afterBusyPaint(callback) {
    setCartBusy(false);
    window.requestAnimationFrame(callback);
  }

  /* The optimistic pending row. Reads the product off the form's data-* attributes — the
     same contract the mockup declares — rather than scraping the surrounding DOM, so it
     works identically from a catalog card, the PDP and the variant sheet. */
  function pendingRow() {
    return cartDialog ? one('[data-cart-pending]', cartDialog) : null;
  }

  function showPendingRow(form, submitter) {
    const row = pendingRow();
    if (!row) return;
    const d = form.dataset;
    const image = one('[data-pending-image]', row);
    const title = one('[data-pending-title]', row);
    const variant = one('[data-pending-variant]', row);
    if (title) title.textContent = d.productTitle || '';
    // The submitter IS the size chip, so its label is the variant with no lookup needed.
    if (variant) variant.textContent = (submitter.textContent || '').trim();
    if (image) {
      if (d.productImage) {
        image.src = d.productImage;
        image.hidden = false;
      } else {
        image.removeAttribute('src');
        image.hidden = true;
      }
    }
    row.hidden = false;
  }

  function hidePendingRow() {
    const row = pendingRow();
    if (row) row.hidden = true;
  }

  function enqueueCartMutation(mutation) {
    return new Promise((resolve, reject) => {
      cartMutationQueue.push({ mutation, resolve, reject });
      drainCartMutationQueue();
    });
  }

  async function drainCartMutationQueue() {
    if (cartRequestInFlight || cartMutationQueue.length === 0) return;
    cartRequestInFlight = true;
    setCartBusy(true);
    const next = cartMutationQueue.shift();
    try {
      next.resolve(await next.mutation());
    } catch (error) {
      next.reject(error);
    } finally {
      cartRequestInFlight = false;
      setCartBusy(false);
      drainCartMutationQueue();
    }
  }

  /* --------------------------------------------------- server-rendered cart state

     Everything shown in the drawer — the lines, the quantities, the item count — comes
     from the returned section HTML. Nothing is derived client-side. The previous mockup
     JS painted a single hardcoded line, which is correct for exactly one cart in the
     world and wrong for every other.
  */

  function applyCartSection(html) {
    const target = one('[data-cart-render]', cartDialog);
    if (!target || typeof html !== 'string' || !html.trim()) return false;
    // The response is the whole section including its shopify-section wrapper; take the
    // matching inner region out of it rather than nesting wrappers on every mutation.
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = parsed.querySelector('[data-cart-render]');
    if (!incoming) return false;
    target.innerHTML = incoming.innerHTML;
    // This swap destroys every node inside the region, including the sale countdown, and
    // replaces them with fresh server-rendered ones that nothing has initialised. Anything
    // needing to re-attach after a cart mutation listens for this rather than the cart
    // module holding a reference to it — the drawer should not need to know a countdown
    // exists. Dispatched on `document` because the old nodes are already detached.
    document.dispatchEvent(new CustomEvent('sr:cart-rendered'));
    const count = one('[data-cart-count-source]', target);
    if (count) {
      const value = count.textContent.trim();
      all('[data-bag-count]').forEach((node) => { node.textContent = value; });
    }
    return true;
  }

  async function refetchCartSection() {
    const response = await fetch(`${url('')}?section_id=${CART_SECTION}`, {
      headers: { Accept: 'text/html' }
    });
    if (!response.ok) return false;
    return applyCartSection(await response.text());
  }

  /* Reconcile after a mutation.

     A cart mutation can return HTTP 200 with the requested section `null`. That means the
     item IS in the cart and only the re-render failed — reporting it as a failed add would
     be a lie. So: one bounded refetch, then a full reload. Never an unbounded retry loop.
  */
  /* The /cart page renders `sr-cart-line` too, with the same mutation buttons. Until
     2026-08-17 nothing re-rendered it, so clicking a stepper there mutated the cart and
     left the page showing the old quantities.

     This needs the section INSTANCE id (`main`, the key in templates/cart.json) — the
     type name `sr-main-cart` is not requestable — and a locale-aware URL, so it comes
     from `routes.cart_url` rather than a hardcoded "/cart" that would break under
     Markets. Both are published as data attributes by the section itself.

     It runs on EVERY mutation while the cart template is showing, not only mutations that
     originated outside the drawer: opening the bag over /cart and stepping a quantity
     there has to update the page behind it too. */
  async function refreshCartPage() {
    const host = one('[data-cart-page]');
    if (!host) return;
    const sectionId = host.dataset.sectionId;
    const base = host.dataset.sectionsUrl || url('cart');
    if (!sectionId) return;
    const joiner = base.indexOf('?') === -1 ? '?' : '&';
    const response = await fetch(
      `${base}${joiner}section_id=${encodeURIComponent(sectionId)}`,
      { headers: { Accept: 'text/html' } }
    );
    if (!response.ok) return;
    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const incoming = parsed.querySelector('[data-cart-page]');
    if (incoming) {
      host.innerHTML = incoming.innerHTML;
      // Same wholesale-replacement hazard as applyCartSection, on the cart PAGE this time.
      // Fired even though today only the drawer carries a countdown: this is the recovery
      // path the drawer swap falls back to, and a re-init that only works on the happy
      // path is the kind of gap that shows up as "the clock froze, but only sometimes".
      document.dispatchEvent(new CustomEvent('sr:cart-rendered'));
    }
  }

  async function reconcile(payload) {
    const html = payload && payload.sections && payload.sections[CART_SECTION];
    if (!applyCartSection(html) && !(await refetchCartSection())) {
      window.location.reload();
      return;
    }
    await refreshCartPage();
  }

  async function cartRequest(path, init) {
    const response = await fetch(url(path), init);
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.description || payload.message || 'Error');
    return payload;
  }

  function addToCart(formData) {
    formData.append('sections', CART_SECTION);
    return cartRequest('cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    });
  }

  function changeLine(itemKey, quantity) {
    return cartRequest('cart/change.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity, sections: CART_SECTION })
    });
  }

  /* ------------------------------------------------------------------ add to cart */

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();

    // `new FormData(form)` DROPS the pressed submit button's name/value. Quick add puts
    // the variant id on the button, so without the submitter every add posts an empty id
    // and Shopify 422s. This one argument is what the whole feature hangs on.
    const submitter = event.submitter || one('button[type="submit"]', form);
    if (!submitter || submitter.disabled) return;
    const formData = new FormData(form, submitter);
    if (!formData.get('id')) return;   // no size chosen; native validation handles the PDP

    // Collapse the card's quick-add panel — the bag drawer is taking over, and leaving it
    // open means the next tap on that card's bag icon CLOSES the panel instead of opening
    // it, which reads as a dead button.
    const quick = submitter.closest('details.sr-card__quick');
    const focusToken = captureFocusToken(submitter);

    /* Close the picker BEFORE the mutation is queued, not after it resolves.
       The sheet lives inside [data-cart-render], which the section swap replaces
       wholesale — and removing an OPEN <dialog> from the DOM does not fire `close`, so
       surfaceStack would keep a dangling entry and the scroll lock and inert flags would
       never be released. The page would go quietly unclickable. Closing synchronously
       here, on the same tick as the click, makes that ordering impossible rather than
       merely unlikely. */
    const sheet = submitter.closest('dialog.sr-vp');
    if (sheet) closeSurface(sheet);

    /* Acknowledge the tap IMMEDIATELY (operator 2026-08-18: "things loading into the cart
       take a second"). The drawer opens instantly but its contents arrive with the section
       render on the /cart/add.js response, so until now the shopper watched the PREVIOUS
       state — usually "Nothing in here yet." — for the length of a round trip. Nothing was
       slow; nothing was acknowledged.

       The row is server-rendered and hidden; we only fill and reveal it. `applyCartSection`
       replaces the whole of [data-cart-render], so it disposes of itself on success with no
       teardown to forget — the error path is the only one that has to hide it again. */
    showPendingRow(form, submitter);

    // No trigger when the add came from a sheet: that submitter is about to be replaced
    // by the swap, and recording it would leave closeSurface trying to focus a detached
    // node. `restoreCartFocus` picks a live target from the response instead.
    openSurface(cartDialog, sheet ? null : submitter);
    // Mutation AND reconciliation are ONE queued transaction. Releasing the slot after
    // the mutation alone let the next mutation start while this one's fallback refetch
    // was still in the air — and a stale refetch landing after a newer response silently
    // reverts the drawer to older state.
    enqueueCartMutation(async () => {
      const payload = await addToCart(formData);
      await reconcile(payload);
    })
      .then(() => {
        if (quick) quick.open = false;
        restoreCartFocus(focusToken);
        showToast(document.body.dataset.addedToast);
      })
      .catch((error) => {
        // Hide it FIRST. A failed add that leaves the optimistic row on screen claims an
        // item is in the bag that is not — worse than the delay this row exists to cover.
        hidePendingRow();
        const region = one('[data-cart-state="error"]', cartDialog);
        if (region) {
          region.hidden = false;
          const message = one('[data-cart-error]', region);
          if (message) message.textContent = error.message || 'Error';
        }
        showToast(error.message || 'Error');
      });
  });

  /* ------------------------------------------------- quantity + remove (delegated)

     Delegated because [data-cart-render] is replaced wholesale after every mutation —
     handlers bound directly to the buttons would be discarded on the first change.
  */

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-mutation]');
    if (!button || button.disabled) return;
    const line = button.closest('[data-cart-line]');
    const itemKey = button.dataset.lineKey || (line && line.dataset.itemKey);
    if (!itemKey) return;

    const quantityNode = line ? one('[data-line-quantity]', line) : null;
    const current = quantityNode ? Number(quantityNode.textContent.trim()) || 1 : 1;
    const action = button.dataset.cartMutation;
    const desired = action === 'remove'
      ? 0
      : Math.max(0, current + (action === 'increase' ? 1 : -1));

    // Captured here, synchronously, BEFORE the queue disables this very button.
    const focusToken = captureFocusToken(button);

    enqueueCartMutation(async () => {
      const payload = await changeLine(itemKey, desired);
      await reconcile(payload);
    })
      .then(() => restoreCartFocus(focusToken))
      .catch((error) => showToast(error.message || 'Error'));
  });

  /* ------------------------------------------------------------------ PDP sizes */

  all('[data-product-form]').forEach((form) => {
    const label = one('[data-atc-label]', form);
    const button = one('[data-atc]', form);
    if (!label || !button) return;              // quick-add forms have no label to swap
    const ready = button.dataset.atcReady;
    const sync = () => {
      if (one('input[name="id"]:checked', form) && ready) label.textContent = ready;
    };
    all('input[name="id"]', form).forEach((radio) => radio.addEventListener('change', sync));
    const hash = window.location.hash.slice(1);
    if (hash) {
      const target = form.querySelector(`input[name="id"]#${CSS.escape(hash)}`);
      if (target && !target.disabled) target.checked = true;
    }
    sync();
  });

  /* ------------------------------------------------------------------ pdp gallery

     The carousel is CSS scroll-snap. Swipe, trackpad and shift-wheel already work with
     this file deleted, and everything below is enhancement on top of a control that
     functions. That asymmetry is deliberate:

       - DOTS ship in the Liquid as real anchors to #sr-shot-N, so they navigate with no
         JS at all. We intercept them only to suppress the raw hash jump, which scrolls
         the DOCUMENT to reveal the target as well as scrolling the container.
       - ARROWS are not in the markup. A server-rendered arrow needs script to move a
         scroll container, so without JS it would be a button that does nothing — the
         same B-041 trap as a no-JS fallback only JS can build. Created here or not at all.
  */
  all('[data-gallery]').forEach((track) => {
    const slides = all('.sr-gallery__slide', track);
    if (slides.length < 2) return;          // one image is not a carousel
    const media = track.closest('.sr-pdp__media') || track.parentElement;
    const dots = all('.sr-gallery__dot', media);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const goTo = (i) => {
      const slide = slides[Math.max(0, Math.min(slides.length - 1, i))];
      if (!slide) return;
      // NOT scrollTo({ left: slide.offsetLeft }) — offsetLeft is measured from
      // offsetParent, not from the scroll container, so it overshoots as soon as the
      // gallery sits inside the shell/grid. scrollIntoView is container-relative.
      slide.scrollIntoView({
        inline: 'start',
        block: 'nearest',                   // never scroll the page vertically
        behavior: reduced ? 'auto' : 'smooth'
      });
    };

    dots.forEach((dot, i) => {
      dot.addEventListener('click', (event) => { event.preventDefault(); goTo(i); });
    });

    const arrow = (dir, label, path) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `sr-gallery__nav sr-gallery__nav--${dir}`;
      button.setAttribute('aria-label', label);
      button.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" '
        + 'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" '
        + `aria-hidden="true" focusable="false"><path d="${path}"/></svg>`;
      media.appendChild(button);
      return button;
    };
    const prev = arrow('prev', 'Previous image', 'M15 4 7 12l8 8');
    const next = arrow('next', 'Next image', 'M9 4l8 8-8 8');

    let index = 0;
    const sync = () => {
      dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      // Inert at the ends rather than removed — removing the control the user is
      // currently focused on drops focus to <body> mid-interaction.
      prev.setAttribute('aria-disabled', String(index === 0));
      next.setAttribute('aria-disabled', String(index === slides.length - 1));
    };

    // aria-disabled is advisory: it tells assistive tech, it does not stop activation.
    // Guard the behaviour too, or an "off" arrow still scrolls past the end.
    prev.addEventListener('click', () => {
      if (prev.getAttribute('aria-disabled') !== 'true') goTo(index - 1);
    });
    next.addEventListener('click', () => {
      if (next.getAttribute('aria-disabled') !== 'true') goTo(index + 1);
    });

    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(index - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); goTo(index + 1); }
    });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const i = slides.indexOf(entry.target);
          if (i !== -1) { index = i; sync(); }
        });
      }, { root: track, threshold: 0.6 });
      slides.forEach((slide) => io.observe(slide));
    }
    sync();
  });

  /* ------------------------------------------------------------------ reveal */

  (function reveal() {
    const items = all('.sr-reveal');
    if (!items.length) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.02 });
    items.forEach((el) => io.observe(el));
    window.setTimeout(() => items.forEach((el) => el.classList.add('is-in')), 1200);
  })();
})();

/* Origin film — swap the poster for a real <video> only once someone asks for it.
   The element ships with preload="none" and no src at all, so an untouched page
   costs one poster image instead of a multi-megabyte MP4. */
(() => {
  'use strict';

  document.querySelectorAll('[data-sr-film]').forEach((figure) => {
    const trigger = figure.querySelector('[data-sr-film-play]');
    const video = figure.querySelector('video');
    if (!trigger || !video) return;

    trigger.addEventListener('click', () => {
      const addSource = (url, type) => {
        if (!url) return false;
        const source = document.createElement('source');
        source.src = url;
        source.type = type;
        video.appendChild(source);
        return true;
      };

      // MP4 only, deliberately. Shopify also publishes an HLS playlist, but
      // Chromium answers canPlayType('application/vnd.apple.mpegurl') with
      // "maybe" and then cannot decode it — offering HLS at all makes Chromium
      // select it and stall at readyState 0. Progressive MP4 plays everywhere.
      //
      // The rendition is sized to the box the film actually occupies, not the
      // viewport — it lives in a column, so viewport width would over-serve.
      // Measure the figure, not the video: the video is still hidden at this
      // point and a hidden element measures 0, which would always pick SD.
      //
      // CSS width, deliberately not multiplied by devicePixelRatio. A phone at
      // DPR 3 would read a 334px column as ~1000px and pull the 18MB file down a
      // mobile connection; moving film tolerates far less detail than a still,
      // so bandwidth wins over density here.
      const box = figure.getBoundingClientRect().width;
      const conn = navigator.connection || {};
      const thrifty = conn.saveData === true || /^([23]g|slow-2g)$/.test(conn.effectiveType || '');
      const sd = video.dataset.sd;
      const hd = video.dataset.hd;
      const wantsHd = box > 600 && !thrifty;
      addSource(wantsHd ? (hd || sd) : (sd || hd), 'video/mp4');

      if (!video.firstElementChild) return;

      video.load();
      video.hidden = false;
      trigger.hidden = true;
      video.focus({ preventScroll: true });
      // Autoplay-with-sound is allowed here because the click IS the gesture.
      const played = video.play();
      if (played && played.catch) played.catch(() => {});
    }, { once: true });
  });
})();

/* Sale countdown — ticks every clock on the page and retires each on its own deadline.

   MULTI-INSTANCE. There are up to three on a page (announcement bar, PDP, cart drawer).
   The first version of this used querySelector and a single module-level timer, which
   would have ticked the announcement bar and left the other two frozen at their
   server-rendered value — a stopped clock that still looks authoritative.

   RE-SCANNABLE. The cart drawer's [data-cart-render] region is replaced wholesale on
   every mutation, so a countdown inside it is destroyed and a fresh, uninitialised one
   takes its place. `scan()` is therefore idempotent and re-runs on the sr:cart-rendered
   event: already-live nodes are skipped via a WeakSet, new ones get adopted.

   The clocks are already correct when they arrive — Liquid server-renders the real
   remaining time. This only keeps them moving. */
(() => {
  'use strict';

  const live = new WeakSet();
  const pad = (n) => String(n).padStart(2, '0');

  function retire(el) {
    // Drop the hook attribute FIRST so a concurrent scan() cannot re-adopt this node.
    el.removeAttribute('data-sr-countdown');
    const host = el.closest('[data-sr-countdown-host]');
    if (host && host.dataset.slogan) {
      // Announcement bar: swap to the slogan in place.
      host.classList.remove('sr-announce--sale');
      host.textContent = host.dataset.slogan;
      return;
    }
    // PDP / cart line: the clock and whatever introduces it simply go away. Removing only
    // the digits would strand a dangling "Ends in" with nothing after it.
    const line = el.closest('[data-sr-sale-line]');
    (line || el).remove();
  }

  function start(el) {
    if (live.has(el)) return;
    const endsAt = Date.parse(el.dataset.endsAt || '');
    // An unparseable deadline must not leave a frozen clock implying a live sale.
    if (!Number.isFinite(endsAt)) { retire(el); return; }
    live.add(el);

    const cells = {
      d: el.querySelector('[data-cd="d"]'), h: el.querySelector('[data-cd="h"]'),
      m: el.querySelector('[data-cd="m"]'), s: el.querySelector('[data-cd="s"]'),
    };
    const region = el.querySelector('[data-sr-countdown-live]');
    let lastMinute = -1;
    let timer = null;

    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

    function tick() {
      // Recomputed from the clock every tick, never decremented: a decrementing counter
      // drifts whenever the tab is throttled, and phones throttle hard. A bar left open in
      // a background tab would keep promising time that had already run out.
      const left = Math.floor((endsAt - Date.now()) / 1000);

      // The node can be torn out from under us by a cart re-render. Stop rather than tick
      // a detached element forever — that is a leak with a heartbeat.
      if (!el.isConnected) { stop(); live.delete(el); return; }
      if (left <= 0) { stop(); retire(el); return; }

      const d = Math.floor(left / 86400);
      const h = Math.floor((left % 86400) / 3600);
      const m = Math.floor((left % 3600) / 60);
      if (cells.d) cells.d.textContent = String(d);
      if (cells.h) cells.h.textContent = pad(h);
      if (cells.m) cells.m.textContent = pad(m);
      if (cells.s) cells.s.textContent = pad(left % 60);

      if (region && m !== lastMinute) {
        lastMinute = m;
        const parts = [];
        if (d) parts.push(d + (d === 1 ? ' day' : ' days'));
        if (d || h) parts.push(h + (h === 1 ? ' hour' : ' hours'));
        parts.push(m + (m === 1 ? ' minute' : ' minutes'));
        region.textContent = parts.join(', ') + ' left';
      }
    }

    tick();
    if (el.isConnected && el.hasAttribute('data-sr-countdown')) {
      timer = setInterval(tick, 1000);
    }
  }

  const scan = () => document.querySelectorAll('[data-sr-countdown]').forEach(start);

  // BFCache restores the JS heap but not wall-clock progress, so a shopper tapping Back
  // would otherwise meet a clock frozen where they left it (SITE_GUIDE trap 5).
  window.addEventListener('pageshow', (e) => { if (e.persisted) scan(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scan(); });
  document.addEventListener('sr:cart-rendered', scan);

  scan();
})();
